import { ImportAssetsResponse, TargetApiResponse, TargetCreateType, TargetResult } from '../../app/modules/types/target'
import { apiClient } from '../client'



/**
 * Assets API endpoints (formerly Targets)
 * Migrated from /targets/ to /assets/ endpoints
 */
export const targetsApi = {
  /**
   * Get assets list
   */
  getList: async (params?: string): Promise<TargetApiResponse> => {
    const url = params ? `/assets/?${params}` : '/assets/'
    const { data } = await apiClient.get(url)
    return data
  },

  /**
   * Get asset by ID
   */
  getById: async (targetId: string): Promise<TargetResult> => {
    const { data } = await apiClient.get(`/assets/${targetId}/`)
    return data
  },

  /**
   * Create a new asset
   * @param payload - Object containing { seed: string } where seed is domain/IP/CIDR/URL
   */
  create: async (payload: { seed: string }): Promise<TargetResult> => {
    const { data } = await apiClient.post('/assets/create/', payload)
    return data
  },

  /**
   * Get-or-create many assets at once, for the paste-a-list flow.
   *
   * This is the ONLY way a hand-typed target becomes scannable: `POST /scan/`
   * never creates assets, so an unknown seed sent straight to it is a 400
   * telling the user to import it first. Import first, select second, scan
   * third.
   *
   * @param seeds 1..500 already-split, already-trimmed, already-de-duplicated
   * seed strings (see `parseSeedList`). All must be of ONE type — a mixed list
   * is a 400 that writes nothing.
   */
  importAssets: async (seeds: string[]): Promise<ImportAssetsResponse> => {
    const { data } = await apiClient.post('/assets/import/', { seeds })
    return data
  },

  /**
   * Update asset
   */
  update: async (targetId: string, payload: any): Promise<TargetResult> => {
    const { data } = await apiClient.patch(`/assets/${targetId}/`, payload)
    return data
  },

  /**
   * Delete asset
   */
  delete: async (targetId: string): Promise<void> => {
    await apiClient.delete(`/assets/${targetId}/`)
  },

  /**
   * Get verification code for an asset
   */
  getVerificationCode: async (targetId: string): Promise<any> => {
    const { data } = await apiClient.get(`/assets/${targetId}/verification-code/`)
    return data
  },

  /**
   * Verify asset DNS
   */
  verifyDNS: async (targetId: string): Promise<any> => {
    const { data } = await apiClient.get(`/assets/${targetId}/dns-verify/`)
    return data
  },

  /**
   * Verify asset file
   */
  verifyFile: async (targetId: string): Promise<any> => {
    const { data } = await apiClient.get(`/assets/${targetId}/file-verify/`)
    return data
  },

  // LEGACY: kept from pre-orchestrator version, not present in reference project.
  // Used by workflow↔target compatibility filtering in CreateScan.tsx.
  getWorkflowAvailableTypes: async (workflowId: string): Promise<any> => {
    const { data } = await apiClient.get(`/workflow/${workflowId}/available-types/`)
    return data
  },
}
